/**
 * Schémas Zod — Document Links.
 *
 * Source DB : `app.document_links` (migration 0021, audit trigger câblé 0021:559).
 *
 * Lecture seule depuis 9.F. Chunk 9.J ajoute Create (POST) + Delete (hard
 * DELETE — table sans `deleted_at` column par design 0021). Gating Couche 2
 * 9.H.5 actif : `document_link.create` default `agent_allowed=true` (seedée
 * 9.H.5) ; `document_link.delete` default `agent_allowed=false` (seedée
 * via 0036 — asymétrie pédagogique avec create, parité MI 9.I.2 destructive).
 *
 * Note Q3 PO 9.F : raw row only — pas d'enrichissement file_name/mime_type
 * dans la réponse (round-trip via /v1/files/:id si besoin).
 */

import { z } from "zod";

import { PaginationSchema, UuidSchema } from "./common.js";

// -----------------------------------------------------------------------------
// Enum AttachableKind (miroir DB)
// -----------------------------------------------------------------------------
//
// 5 actifs en 9.E.1 ; 4 réservés (task/report/offer/employee_census) refusés
// par le trigger SQL `app.validate_attachable_exists()`. On expose les 9
// dans le schema Zod pour rester aligné avec l'enum DB ; côté lecture, seuls
// les 5 actifs apparaîtront en pratique.

export const AttachableKindSchema = z.enum([
  // Actifs
  "company",
  "project",
  "quote_request",
  "submission",
  "intake_event",
  // Réservés (ne peuvent pas être insérés mais peuvent apparaître dans des
  // dumps SQL / rows historiques côté super_admin)
  "task",
  "report",
  "offer",
  "employee_census",
]);
export type AttachableKind = z.infer<typeof AttachableKindSchema>;

// -----------------------------------------------------------------------------
// Row (lecture)
// -----------------------------------------------------------------------------

export const DocumentLinkRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  file_id: UuidSchema,

  attachable_kind: AttachableKindSchema,
  attachable_id: UuidSchema,

  link_role: z.string(),
  classification: z.record(z.unknown()).nullable(),
  display_name: z.string().nullable(),

  created_at: z.string(),
  created_by_user: UuidSchema.nullable(),
  created_by_agent: UuidSchema.nullable(),

  // 9.M.7 — Forensic visible (variant `created`).
  created_by_agent_display_name: z.string().nullable().optional(),
});
export type DocumentLinkRow = z.infer<typeof DocumentLinkRowSchema>;

// -----------------------------------------------------------------------------
// Filtres listing — GET /v1/document-links
// -----------------------------------------------------------------------------
//
// Q5 PO (révisé 9.F.1 — F-01) : au moins UN filtre est désormais OBLIGATOIRE
//   côté API ET côté tool MCP. Les filtres restent individuellement optionnels
//   au niveau de l'objet (un caller peut passer `attachable_kind` seul, ou
//   `file_id` seul, etc.) ; le `.refine()` exige la présence d'au moins un.
//
// Le tool MCP `list_document_links` (apps/mcp/src/tools.ts) garde son propre
// schema inline équivalent — pas d'import croisé.

export const DocumentLinkFilterSchema = z.object({
  file_id: UuidSchema.optional(),
  attachable_kind: AttachableKindSchema.optional(),
  attachable_id: UuidSchema.optional(),
});
export type DocumentLinkFilter = z.infer<typeof DocumentLinkFilterSchema>;

/**
 * Schema complet pour `GET /v1/document-links` :
 * pagination + filtres + refine "au moins un filtre".
 *
 * Note : ce schema avec `.refine()` n'est PAS passé à `@hono/zod-validator`
 * dans la route API — zValidator intercepterait le `ZodError` avant nos
 * handlers et renverrait son format propre (`{success:false, error:{issues}}`)
 * au lieu de notre enveloppe standard `{ok:false, error:{code,message}}`.
 *
 * Côté API (apps/api/src/routes/document-links.ts), on passe à zValidator le
 * schema NON-refined (`PaginationSchema.merge(DocumentLinkFilterSchema)`) et
 * la règle "au moins un filtre" est appliquée dans le handler via
 * `ApiErrors.badRequest` → format 400 `validation_error` cohérent.
 *
 * `DocumentLinkListQuerySchema` reste exporté pour le typage et tout
 * consommateur qui voudrait valider strictement le triplet (ex. tests,
 * MCP futur, SDK client).
 */
export const DocumentLinkListQuerySchema = PaginationSchema.merge(DocumentLinkFilterSchema).refine(
  (q) =>
    q.file_id !== undefined || q.attachable_kind !== undefined || q.attachable_id !== undefined,
  {
    // 9.K.13 (D58.1-c refines techniques) — wording EN technique (P-2
    // ADR 0027 fallback dev). User-facing bilingue côté handler API via
    // `bilingualHttpError("DOCUMENT_LINKS_FILTER_REQUIRED", 400, ...)`
    // (déjà câblé 9.K.9 dans `apps/api/src/routes/document-links.ts`).
    message: "At least one filter is required: file_id, attachable_kind, or attachable_id.",
    path: ["_filters"],
  },
);
export type DocumentLinkListQuery = z.infer<typeof DocumentLinkListQuerySchema>;

// -----------------------------------------------------------------------------
// Create (Chunk 9.J)
// -----------------------------------------------------------------------------
//
// Décisions PO 9.J :
//   Q2 — DELETE Phase 1 = hard DELETE (table sans `deleted_at` column, design
//        0021). Audit `changes.before` forensic preserve.
//   Q5 — Pre-check handler obligatoire (SELECT file + SELECT attachable cible)
//        pour erreurs ciblées (FILE_NOT_FOUND vs ATTACHABLE_NOT_FOUND distincts).
//        Trigger DB `validate_attachable_exists()` reste défense finale.
//   Q3 — `document_link.delete` default policy `agent_allowed=false`. `create`
//        reste `agent_allowed=true` (seedée 9.H.5, action interne non sensitive).
//
// Enum strict 5 kinds actifs (rejet Zod-side des 4 réservés — code dédié
// `DOCUMENT_LINKS_KIND_RESERVED` 422 plutôt que de laisser le trigger DB
// raise une exception PG générique).

/**
 * Enum strict des 5 kinds **actifs** acceptés au POST 9.J. Les 4 kinds
 * réservés (`task`, `report`, `offer`, `employee_census`) sont rejetés
 * Zod-side avec `DOCUMENT_LINKS_KIND_RESERVED` 422 (le trigger DB
 * `validate_attachable_exists()` raise sinon une exception PG générique
 * non-déterministe côté wire shape).
 *
 * Distinct de `AttachableKindSchema` qui expose les 9 valeurs pour la
 * lecture (un super_admin peut voir des rows réservés historiques).
 */
export const AttachableKindActiveSchema = z.enum([
  "company",
  "project",
  "quote_request",
  "submission",
  "intake_event",
]);
export type AttachableKindActive = z.infer<typeof AttachableKindActiveSchema>;

/**
 * Body POST `/v1/document-links` — broker-facing.
 *
 * Le handler valide en plus que :
 *   1. `file_id` existe + appartient au tenant (404 DOCUMENT_LINKS_FILE_NOT_FOUND).
 *   2. `(attachable_kind, attachable_id)` pointe vers une row existante
 *      du même tenant (404 DOCUMENT_LINKS_ATTACHABLE_NOT_FOUND).
 *   3. Trigger DB `validate_attachable_exists()` re-vérifie (defense in depth).
 *
 * `created_by_user` est peuplé handler-side via `auth.userId` (si auth.kind
 * === 'user'). `created_by_agent` reste NULL Phase 1 (D30 agent_identities
 * futur ajoutera la FK).
 */
export const CreateDocumentLinkSchema = z.object({
  file_id: UuidSchema,
  attachable_kind: AttachableKindActiveSchema,
  attachable_id: UuidSchema,
  link_role: z.string().min(1).max(80).optional(),
  classification: z.record(z.unknown()).optional(),
  display_name: z.string().min(1).max(200).optional(),
});
export type CreateDocumentLinkInput = z.infer<typeof CreateDocumentLinkSchema>;
